// SCR-STAFF-505 (US-CLN-070، 071؛ BR-CLN-070): تصاویر بالینی قبل/بعد — مجموعه با ناحیه و مرحله، ثبت فقط از دوربین (getUserMedia یا ورودی
// capture) با تصویر راهنمای شفاف (Ghost) از آخرین تصویر هم‌ناحیه و هم‌زاویه، مقایسه کنار هم و لغزنده، و ابطال با دلیل.
// حریم خصوصی: تصویر فقط در حافظه صفحه (Blob و Object URL) نگه داشته می‌شود؛ هیچ localStorage، IndexedDB یا Cache Storage استفاده نمی‌شود و
// Object URLها هنگام بسته‌شدن آزاد می‌شوند. پاسخ سرور no-store است و هر مشاهده در سرور RecordView ثبت می‌شود.
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { unwrap, type ClnImageAngle, type ClnImageItemView, type ClnImageSetView, type ClnImageStage } from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, FormDialog, LoadingState, ReasonDialog, toJalali, toJalaliDateTime, useDisplaySettings } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState } from 'react';
import { ALL_AREAS } from './bodyRegions';
import { beautyKeys, IMAGE_ANGLES, IMAGE_STAGES, SCR_STAFF_505 } from './beautyCommon';
import { ImageToolsDialog } from './ImageTools';

/** تصویر محافظت‌شده: دریافت با توکن، Object URL در حافظه و آزادسازی هنگام Unmount. */
export function useClinicalImage(imageId: string | null) {
  const { tenantApi } = useTenantSession();
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  useEffect(() => {
    if (!imageId) {
      setUrl(null);
      return undefined;
    }
    let cancelled = false;
    let created: string | null = null;
    void tenantApi
      .GET('/api/v1/clinical/images/{id}/content', { params: { path: { id: imageId } }, parseAs: 'blob', cache: 'no-store' })
      .then((result) => {
        if (cancelled) {
          return;
        }
        if (!result.response.ok || typeof URL.createObjectURL !== 'function') {
          setError(result.error ?? new Error(String(result.response.status)));
          return;
        }
        created = URL.createObjectURL(result.data as unknown as Blob);
        setUrl(created);
      })
      .catch((reason: unknown) => !cancelled && setError(reason));
    return () => {
      cancelled = true;
      if (created) {
        URL.revokeObjectURL(created);
      }
    };
  }, [tenantApi, imageId]);
  return { url, error };
}

function ClinicalImage({ imageId, alt, opacity = 1, testId }: { imageId: string; alt: string; opacity?: number; testId?: string }) {
  const { url, error } = useClinicalImage(imageId);
  if (error) {
    return <ErrorState error={error} compact />;
  }
  if (!url) {
    return <Box sx={{ aspectRatio: '3 / 4', bgcolor: 'action.hover', width: '100%' }} />;
  }
  return <Box component="img" src={url} alt={alt} draggable={false} data-testid={testId} sx={{ width: '100%', display: 'block', opacity, objectFit: 'contain', userSelect: 'none' }} />;
}

/** تصویر ثبت‌شده هم‌ناحیه و هم‌زاویه (برای Ghost و مقایسه). */
function latestOf(sets: readonly ClnImageSetView[], area: string, angle: ClnImageAngle, exceptSetId?: string): ClnImageItemView | null {
  for (const set of sets) {
    if (set.area !== area || set.id === exceptSetId) {
      continue;
    }
    const item = set.items.find((candidate) => candidate.angle === angle && !candidate.voided);
    if (item) {
      return item;
    }
  }
  return null;
}

function NewSetDialog({ patientId, encounterId, onClose, onCreated }: { patientId: string; encounterId?: string | null; onClose: () => void; onCreated: (set: ClnImageSetView) => void }) {
  const { t } = useTranslation('cln');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const [area, setArea] = useState('full_face');
  const [stage, setStage] = useState<ClnImageStage>('Before');
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/clinical/patients/{patientId}/image-sets', {
          params: { path: { patientId } },
          headers: { 'Idempotency-Key': key.current() },
          body: { area, stage, encounterId: encounterId ?? null, sessionId: null },
        }),
      ),
    onSettled: (_data, error) => key.settle(error),
    onSuccess: (view) => {
      void queryClient.invalidateQueries({ queryKey: beautyKeys.images(session?.tenantId, patientId) });
      onCreated(view);
    },
  });
  return (
    <FormDialog open titleKey="cln:image.new_set" screenId={SCR_STAFF_505} onSubmit={() => save.mutate()} onClose={onClose} isDirty={false} isSubmitting={save.isPending} submitError={save.error}>
      <TextField select fullWidth label={t('cln:image.area')} value={area} onChange={(event) => setArea(event.target.value)} slotProps={{ htmlInput: { 'data-testid': 'cln-image-area' } }}>
        {ALL_AREAS.map((code) => (
          <MenuItem key={code} value={code}>
            {t(`cln:body_area.${code}`)}
          </MenuItem>
        ))}
      </TextField>
      <TextField select fullWidth label={t('cln:image.stage')} value={stage} onChange={(event) => setStage(event.target.value as ClnImageStage)} slotProps={{ htmlInput: { 'data-testid': 'cln-image-stage' } }}>
        {IMAGE_STAGES.map((option) => (
          <MenuItem key={option} value={option}>
            {t(`cln:image_stage.${option}`)}
          </MenuItem>
        ))}
      </TextField>
    </FormDialog>
  );
}

/** ثبت تصویر فقط از دوربین (BR-CLN-070: انتخاب از گالری مجاز نیست؛ ورودی capture در موبایل دوربین را باز می‌کند). */
function CaptureDialog({ set, ghost, onClose }: { set: ClnImageSetView; ghost: (angle: ClnImageAngle) => ClnImageItemView | null; onClose: () => void }) {
  const { t } = useTranslation('cln');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const inputId = useId();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [angle, setAngle] = useState<ClnImageAngle>('Front');
  const [camera, setCamera] = useState<'starting' | 'live' | 'unavailable'>('starting');
  const [captured, setCaptured] = useState<Blob | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [ghostOn, setGhostOn] = useState(true);

  useEffect(() => {
    const media = typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined;
    if (!media?.getUserMedia) {
      setCamera('unavailable');
      return undefined;
    }
    let stopped = false;
    void media
      .getUserMedia({ video: { facingMode: 'environment' }, audio: false })
      .then((stream) => {
        if (stopped) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
        setCamera('live');
      })
      .catch(() => setCamera('unavailable'));
    return () => {
      stopped = true;
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!captured || typeof URL.createObjectURL !== 'function') {
      setPreview(null);
      return undefined;
    }
    const url = URL.createObjectURL(captured);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [captured]);

  const snap = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) {
      return;
    }
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d')?.drawImage(video, 0, 0);
    canvas.toBlob((blob) => blob && setCaptured(blob), 'image/jpeg', 0.92);
  };

  const upload = useMutation({
    mutationFn: (blob: Blob) => {
      const form = new FormData();
      form.append('file', blob, 'capture.jpg');
      form.append('angle', angle);
      return unwrap(
        tenantApi.POST('/api/v1/clinical/image-sets/{id}/images', {
          params: { path: { id: set.id } },
          headers: { 'Idempotency-Key': key.current() },
          body: form as never,
          bodySerializer: (body: unknown) => body as FormData,
        }),
      );
    },
    onSettled: (_data, error) => key.settle(error),
    onSuccess: () => {
      setCaptured(null);
      void queryClient.invalidateQueries({ queryKey: beautyKeys.images(session?.tenantId, set.patientId) });
    },
  });
  const guide = ghost(angle);

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth data-screen-id={SCR_STAFF_505} data-testid="cln-image-capture">
      <DialogTitle>
        {t('cln:image.capture')} · {t(`cln:body_area.${set.area}`)} · {t(`cln:image_stage.${set.stage}`)}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1.5}>
          <TextField select fullWidth size="small" label={t('cln:image.angle')} value={angle} onChange={(event) => setAngle(event.target.value as ClnImageAngle)} slotProps={{ htmlInput: { 'data-testid': 'cln-image-angle' } }}>
            {IMAGE_ANGLES.map((option) => (
              <MenuItem key={option} value={option}>
                {t(`cln:image_angle.${option}`)}
              </MenuItem>
            ))}
          </TextField>
          <Box sx={{ position: 'relative', bgcolor: 'common.black', minHeight: 200 }}>
            {preview ? (
              <Box component="img" src={preview} alt={t('cln:image.preview')} sx={{ width: '100%', display: 'block' }} data-testid="cln-image-preview" />
            ) : (
              <Box component="video" ref={videoRef} autoPlay playsInline muted sx={{ width: '100%', display: camera === 'live' ? 'block' : 'none' }} />
            )}
            {!preview && guide && ghostOn && (
              <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none' }} data-testid="cln-image-ghost">
                <ClinicalImage imageId={guide.id} alt={t('cln:image.ghost')} opacity={0.35} />
              </Box>
            )}
          </Box>
          {camera === 'unavailable' && !preview && <Alert severity="info">{t('cln:image.camera_unavailable')}</Alert>}
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {guide && (
              <ToggleButton size="small" value="ghost" selected={ghostOn} onChange={() => setGhostOn((value) => !value)}>
                {t('cln:image.ghost')}
              </ToggleButton>
            )}
            {camera === 'live' && !preview && (
              <Button variant="contained" onClick={snap} data-testid="cln-image-snap">
                {t('cln:image.snap')}
              </Button>
            )}
            {!preview && (
              <Button component="label" htmlFor={inputId} variant="outlined">
                {t('cln:image.device_camera')}
                <input
                  id={inputId}
                  hidden
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  capture="environment"
                  data-testid="cln-image-file"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    event.target.value = '';
                    if (file) {
                      setCaptured(file);
                    }
                  }}
                />
              </Button>
            )}
            {preview && (
              <>
                <Button variant="contained" onClick={() => captured && upload.mutate(captured)} disabled={upload.isPending} data-testid="cln-image-upload">
                  {t('cln:image.save')}
                </Button>
                <Button onClick={() => setCaptured(null)} disabled={upload.isPending}>
                  {t('cln:image.retake')}
                </Button>
              </>
            )}
          </Stack>
          {upload.error ? <ErrorState error={upload.error} compact /> : null}
          {upload.isSuccess && !preview && <Alert severity="success">{t('cln:image.saved')}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('cln:common.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}

/** مقایسه تصاویر هم‌ناحیه و هم‌زاویه (AC-CLN-071.1): کنار هم، لغزنده یا شبکه همه تصاویر همان ناحیه و زاویه به ترتیب زمان. */
function CompareDialog({
  left,
  right,
  series,
  onClose,
}: {
  left: { id: string; label: string };
  right: { id: string; label: string };
  series: readonly { id: string; label: string }[];
  onClose: () => void;
}) {
  const { t } = useTranslation('cln');
  const [mode, setMode] = useState<'side' | 'slider' | 'grid'>('side');
  const [split, setSplit] = useState(50);
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth data-screen-id={SCR_STAFF_505} data-testid="cln-image-compare">
      <DialogTitle>{t('cln:image.compare')}</DialogTitle>
      <DialogContent>
        <ToggleButtonGroup size="small" exclusive value={mode} onChange={(_event, value: typeof mode | null) => value && setMode(value)} sx={{ mb: 1 }}>
          <ToggleButton value="side" data-testid="cln-compare-side">
            {t('cln:image.side_by_side')}
          </ToggleButton>
          <ToggleButton value="slider" data-testid="cln-compare-slider">
            {t('cln:image.slider')}
          </ToggleButton>
          <ToggleButton value="grid" data-testid="cln-compare-grid">
            {t('cln:image.grid')}
          </ToggleButton>
        </ToggleButtonGroup>
        {mode === 'grid' ? (
          <Grid container spacing={1} data-testid="cln-compare-grid-view">
            {series.map((item) => (
              <Grid key={item.id} size={{ xs: 6, sm: 4 }}>
                <Typography variant="caption">{item.label}</Typography>
                <ClinicalImage imageId={item.id} alt={item.label} testId="cln-compare-image" />
              </Grid>
            ))}
          </Grid>
        ) : mode === 'side' ? (
          <Grid container spacing={1}>
            {[left, right].map((item) => (
              <Grid key={item.id} size={6}>
                <Typography variant="caption">{item.label}</Typography>
                <ClinicalImage imageId={item.id} alt={item.label} testId="cln-compare-image" />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Stack spacing={1}>
            <Box sx={{ position: 'relative' }}>
              <ClinicalImage imageId={right.id} alt={right.label} testId="cln-compare-image" />
              <Box sx={{ position: 'absolute', inset: 0, clipPath: `inset(0 ${100 - split}% 0 0)` }}>
                <ClinicalImage imageId={left.id} alt={left.label} testId="cln-compare-image" />
              </Box>
            </Box>
            <Slider value={split} onChange={(_event, value) => setSplit(value as number)} aria-label={t('cln:image.slider')} />
            <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
              <Typography variant="caption">{left.label}</Typography>
              <Typography variant="caption">{right.label}</Typography>
            </Stack>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('cln:common.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}

export interface ClinicalImagesPanelProps {
  patientId: string;
  encounterId?: string | null;
}

export function ClinicalImagesPanel({ patientId, encounterId }: ClinicalImagesPanelProps) {
  const { t } = useTranslation('cln');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const { timeZone } = useDisplaySettings();
  const queryClient = useQueryClient();
  const [area, setArea] = useState('');
  const [angle, setAngle] = useState<ClnImageAngle | ''>('');
  const [creating, setCreating] = useState(false);
  const [capturing, setCapturing] = useState<ClnImageSetView | null>(null);
  const [selected, setSelected] = useState<{ id: string; label: string }[]>([]);
  const [comparing, setComparing] = useState(false);
  const [voiding, setVoiding] = useState<string | null>(null);
  const [shown, setShown] = useState<Record<string, boolean>>({});
  // AP-CLN-04 (US-CLN-072، 073): حاشیه‌نویسی، خروجی با رضایت و پوشش، ارسال به پرتال.
  const [tools, setTools] = useState<{ id: string; area: string; label: string } | null>(null);
  const sets = useQuery({
    queryKey: [...beautyKeys.images(session?.tenantId, patientId), area, angle],
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/clinical/patients/{patientId}/image-sets', {
          params: { path: { patientId }, query: { area: area || undefined, angle: angle || undefined } },
        }),
      ),
  });
  const voidImage = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => unwrap(tenantApi.POST('/api/v1/clinical/images/{id}:void', { params: { path: { id } }, body: { reason } })),
    onSuccess: () => {
      setVoiding(null);
      void queryClient.invalidateQueries({ queryKey: beautyKeys.images(session?.tenantId, patientId) });
    },
  });
  const full = access.accessMode === 'Full';
  const canCapture = full && access.can('cln.image.capture');
  const items = sets.data?.items ?? [];
  // شبکه: همه تصاویر معتبر هم‌ناحیه و هم‌زاویه تصویر نخست، قدیمی به جدید.
  const seriesOf = (imageId: string) => {
    const owner = items.find((set) => set.items.some((item) => item.id === imageId));
    const angleOf = owner?.items.find((item) => item.id === imageId)?.angle;
    return items
      .filter((set) => set.area === owner?.area)
      .flatMap((set) => set.items.filter((item) => item.angle === angleOf && !item.voided).map((item) => ({ set, item })))
      .sort((a, b) => a.item.capturedAt.localeCompare(b.item.capturedAt))
      .map(({ set, item }) => ({ id: item.id, label: `${t(`cln:image_stage.${set.stage}`)} · ${toJalali(item.capturedAt)}` }));
  };
  const toggle = (id: string, label: string) =>
    setSelected((current) => (current.some((item) => item.id === id) ? current.filter((item) => item.id !== id) : [...current.slice(-1), { id, label }]));

  return (
    <Stack spacing={1.5} data-screen-id={SCR_STAFF_505} data-testid="cln-images-panel">
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: { sm: 'center' } }}>
        <TextField select size="small" label={t('cln:image.area')} value={area} onChange={(event) => setArea(event.target.value)} sx={{ minWidth: 160 }} slotProps={{ htmlInput: { 'data-testid': 'cln-image-filter-area' } }}>
          <MenuItem value="">{t('cln:common.all')}</MenuItem>
          {ALL_AREAS.map((code) => (
            <MenuItem key={code} value={code}>
              {t(`cln:body_area.${code}`)}
            </MenuItem>
          ))}
        </TextField>
        <TextField select size="small" label={t('cln:image.angle')} value={angle} onChange={(event) => setAngle(event.target.value as ClnImageAngle | '')} sx={{ minWidth: 140 }} slotProps={{ htmlInput: { 'data-testid': 'cln-image-filter-angle' } }}>
          <MenuItem value="">{t('cln:common.all')}</MenuItem>
          {IMAGE_ANGLES.map((option) => (
            <MenuItem key={option} value={option}>
              {t(`cln:image_angle.${option}`)}
            </MenuItem>
          ))}
        </TextField>
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="outlined" disabled={selected.length !== 2} onClick={() => setComparing(true)} data-testid="cln-image-compare-open">
          {t('cln:image.compare')}
        </Button>
        {canCapture && (
          <Button variant="contained" onClick={() => setCreating(true)} data-testid="cln-image-new-set">
            {t('cln:image.new_set')}
          </Button>
        )}
      </Stack>
      <Typography variant="caption" color="text.secondary">
        {t('cln:image.privacy')}
      </Typography>
      {sets.isPending && <LoadingState rows={2} />}
      {sets.error ? <ErrorState error={sets.error} compact onRetry={() => void sets.refetch()} /> : null}
      {!sets.isPending && items.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {t('cln:image.empty')}
        </Typography>
      )}
      {items.map((set) => (
        <Paper key={set.id} variant="outlined" sx={{ p: 1.5 }} data-testid="cln-image-set" data-set-id={set.id}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }} useFlexGap flexWrap="wrap">
            <Typography variant="subtitle2">
              {t(`cln:body_area.${set.area}`)} · {t(`cln:image_stage.${set.stage}`)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {toJalali(set.takenOn)}
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            {canCapture && (
              <Button size="small" onClick={() => setCapturing(set)} data-testid="cln-image-capture-open">
                {t('cln:image.capture')}
              </Button>
            )}
          </Stack>
          <Grid container spacing={1}>
            {set.items.map((item) => {
              const label = `${t(`cln:body_area.${set.area}`)} · ${t(`cln:image_stage.${set.stage}`)} · ${t(`cln:image_angle.${item.angle}`)} · ${toJalali(item.capturedAt)}`;
              const isSelected = selected.some((entry) => entry.id === item.id);
              return (
                <Grid key={item.id} size={{ xs: 6, sm: 4, md: 3 }}>
                  <Paper variant="outlined" sx={{ p: 0.5, borderColor: isSelected ? 'primary.main' : undefined, opacity: item.voided ? 0.5 : 1 }} data-testid="cln-image-item" data-image-id={item.id}>
                    {shown[item.id] && !item.voided ? (
                      <ClinicalImage imageId={item.id} alt={label} testId="cln-image-thumb" />
                    ) : (
                      <Button fullWidth sx={{ aspectRatio: '3 / 4' }} disabled={item.voided} onClick={() => setShown((current) => ({ ...current, [item.id]: true }))} data-testid="cln-image-show">
                        {item.voided ? t('cln:image.voided') : t('cln:image.show')}
                      </Button>
                    )}
                    <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', mt: 0.5 }} useFlexGap flexWrap="wrap">
                      <Chip size="small" label={t(`cln:image_angle.${item.angle}`)} />
                      <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
                        {toJalaliDateTime(item.capturedAt, { timeZone })}
                      </Typography>
                      {!item.voided && (
                        <Button size="small" onClick={() => setTools({ id: item.id, area: set.area, label })} data-testid="cln-image-tools-open">
                          {t('cln:image_tools.open')}
                        </Button>
                      )}
                      {!item.voided && (
                        <Button size="small" onClick={() => toggle(item.id, label)} data-testid="cln-image-select" aria-pressed={isSelected}>
                          {isSelected ? t('cln:image.selected') : t('cln:image.select')}
                        </Button>
                      )}
                      {canCapture && !item.voided && (
                        <Button size="small" color="error" onClick={() => setVoiding(item.id)} data-testid="cln-image-void">
                          {t('cln:image.void')}
                        </Button>
                      )}
                    </Stack>
                    {item.voided && item.voidReason && (
                      <Typography variant="caption" color="text.secondary">
                        {item.voidReason}
                      </Typography>
                    )}
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Paper>
      ))}
      {creating && (
        <NewSetDialog
          patientId={patientId}
          encounterId={encounterId}
          onClose={() => setCreating(false)}
          onCreated={(set) => {
            setCreating(false);
            setCapturing(set);
          }}
        />
      )}
      {capturing && <CaptureDialog set={capturing} ghost={(value) => latestOf(items, capturing.area, value, capturing.id)} onClose={() => setCapturing(null)} />}
      {comparing && selected.length === 2 && selected[0] && selected[1] && (
        <CompareDialog left={selected[0]} right={selected[1]} series={seriesOf(selected[0].id)} onClose={() => setComparing(false)} />
      )}
      {tools && <ImageToolsDialog imageId={tools.id} patientId={patientId} area={tools.area} label={tools.label} onClose={() => setTools(null)} />}
      <ReasonDialog
        open={voiding !== null}
        titleKey="cln:image.void"
        messageKey="cln:image.void_message"
        busy={voidImage.isPending}
        error={voidImage.error}
        onConfirm={(reason) => voiding && voidImage.mutate({ id: voiding, reason })}
        onCancel={() => setVoiding(null)}
      />
    </Stack>
  );
}

/** دیالوگ تصاویر از مواجهه (اقدام سریع «تصویر»). */
export function ClinicalImagesDialog({ patientId, encounterId, onClose }: { patientId: string; encounterId?: string | null; onClose: () => void }) {
  const { t } = useTranslation('cln');
  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{t('cln:image.title')}</DialogTitle>
      <DialogContent>
        <ClinicalImagesPanel patientId={patientId} encounterId={encounterId} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('cln:common.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}
